import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { can, inboxApi, PERMISSION, useAuth } from '@wa/shared';
import { SECTIONS, maySeeSection } from '@/settings/sections';
import { AvailabilityToggle } from '@/components/AvailabilityToggle';

/**
 * The left rail, as icons.
 *
 * <b>This is the change that dissolves a constraint rather than working around
 * it.</b> The labelled rail this replaces held five destinations and five was a
 * hard ceiling — a sixth made "Contacts" and "Campaigns" overlap at 360px. Billing
 * was pushed into the header's balance chip and Routing became a tab on Team, both
 * for that reason and both documented as such. Neither was a design decision; both
 * were a 220px column with words in it.
 *
 * At 56px with no labels the rail holds fifteen comfortably, so Billing and Routing
 * come back to the top level and the next feature that wants a place here can have
 * one.
 *
 * <b>Icon-only navigation is a memory test, and the tooltip is what stops it being
 * one.</b> Every item shows its name on hover and on focus, and that same string is
 * the accessible name — so the rail reads identically to a screen reader and to
 * somebody on their first morning. A rail of unlabelled glyphs without this is the
 * single most common way an app like it becomes unusable.
 *
 * <b>It widens on hover and collapses again when the pointer leaves</b>, which is
 * what the design being matched does — 64px of glyphs at rest, 240px of labelled
 * rows while the pointer is in it. It expands as an <i>overlay</i> and never
 * pushes the screen sideways: a four-column chat screen that reflows every time a
 * pointer crosses the left edge is unusable, and it is why the collapsed strip
 * keeps its width in the layout while the panel floats above it.
 *
 * Three things this has to get right, none of which are the animation:
 *
 * - <b>Only where hovering means something.</b> Guarded on `(hover: hover) and
 *   (pointer: fine)`. On a touch screen hover is sticky — the panel would open on
 *   the tap that navigated and stay open over the screen you just asked for.
 * - <b>The keyboard gets the same thing.</b> `focus-within` expands it too, so
 *   somebody tabbing through is not reading unlabelled glyphs.
 * - <b>The tooltips go away while it is open.</b> A label beside a row that
 *   already says the label is a duplicate that lands on top of the next row.
 *
 * Below `lg` it is still a bottom bar, because a rail of any width is a rail on a
 * phone, and there is no hover to expand it with. There the five-item ceiling is
 * real, so the bar carries five and the rest move into an overflow sheet — the old
 * constraint, kept in the one place it was ever true.
 */
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

interface Item {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Hidden without this. Courtesy, not control — the routes check it again. */
  needs?: string;
  /** A second permission that also opens it. Either is enough. */
  orNeeds?: string;
  /**
   * Opens if any settings section does.
   *
   * Settings lists its sections from one registry, so the rail asks that registry
   * rather than repeating three of its permissions here — which is how a person with
   * only *Manage quick replies* had a section they could open and no way to reach it.
   *
   * It was `anyOf: SECTIONS.map((s) => s.needs)` until a section arrived with no
   * `needs` at all — Notifications, which is every agent's own. That mapped to
   * `undefined`, and `can(user, undefined)` is false, so the entry would have
   * disappeared for exactly the people whose only section it was. Asking the registry
   * through its own helper cannot go wrong that way.
   */
  anySection?: boolean;
  /** Shown in the bottom bar on a phone. The rest go to the overflow sheet. */
  onPhone?: boolean;
  /** A shorter word for the 72px a phone gives each item. Default: the label. */
  phoneLabel?: string;
}

const ITEMS: Item[] = [
  {
    to: '/home', label: 'Home', onPhone: true,
    icon: (
      <>
        <path d="M3.4 8.8 10 3.6l6.6 5.2v6.8a1.4 1.4 0 0 1-1.4 1.4H4.8a1.4 1.4 0 0 1-1.4-1.4z" {...stroke} />
        <path d="M8 17V11h4v6" {...stroke} />
      </>
    ),
  },
  {
    // "Chats", not "Inbox". Same screen, their word — and theirs is the better
    // one: an inbox is a place things arrive, a chat is the thing an agent is
    // actually doing all day.
    to: '/chats', label: 'Chats', onPhone: true,
    icon: (
      <>
        <path d="M3 10.5 5.2 4.6a1.4 1.4 0 0 1 1.3-.9h7a1.4 1.4 0 0 1 1.3.9L17 10.5" {...stroke} />
        <path d="M3 10.5h3.6l1 2.2h4.8l1-2.2H17v4.2a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 14.7z" {...stroke} />
      </>
    ),
  },
  {
    // Their word for the same screen.
    to: '/customers', label: 'Customers', onPhone: true,
    needs: PERMISSION.contactsView,
    icon: (
      <>
        <circle cx="10" cy="6.6" r="2.9" {...stroke} />
        <path d="M4.4 16.4a5.8 5.8 0 0 1 11.2 0" {...stroke} />
      </>
    ),
  },
  {
    // The reference's name and model: a list is the thing, a broadcast is
    // something sent to it. Campaigns, as a table, redirects here.
    to: '/broadcasts', label: 'Broadcast List', phoneLabel: 'Broadcasts', onPhone: true,
    needs: PERMISSION.campaignsView,
    icon: (
      <>
        <path d="M4 8.4v3.2a1 1 0 0 0 1 1h2.2L12 15.6V4.4L7.2 7.4H5a1 1 0 0 0-1 1Z" {...stroke} />
        <path d="M14.6 7.6a3.4 3.4 0 0 1 0 4.8" {...stroke} />
      </>
    ),
  },
  {
    // Between the lists and the schedule, where the reference keeps it. The entry
    // is back because the screen behind it now exists: a client's own team writes,
    // submits and retires templates from here (F162), gated by templates.write for
    // the writing and open to anyone with the inbox for the reading.
    to: '/templates', label: 'Templates',
    needs: PERMISSION.inboxView,
    icon: (
      <>
        <rect x="3.5" y="5" width="13" height="10.5" rx="1.6" {...stroke} />
        <path d="m3.5 6.6 6.5 4.2 6.5-4.2" {...stroke} />
      </>
    ),
  },
  {
    to: '/scheduled-broadcasts', label: 'Scheduled Broadcasts',
    needs: PERMISSION.campaignsView,
    icon: (
      <>
        <rect x="3.5" y="4.5" width="13" height="12" rx="1.6" {...stroke} />
        <path d="M3.5 8.5h13M7 3v3M13 3v3" {...stroke} />
        <circle cx="13" cy="13" r="2.6" {...stroke} fill="var(--color-surface)" />
        <path d="M13 11.8V13l.9.6" {...stroke} />
      </>
    ),
  },
  {
    to: '/reports', label: 'Reports', onPhone: true,
    needs: PERMISSION.reportsView,
    icon: (
      <>
        <path d="M4 16V9.5M8 16V5M12 16v-4.5M16 16V7.5" {...stroke} />
        <path d="M3 17.4h14" {...stroke} />
      </>
    ),
  },
  {
    // The WhatsApp Shop: products, prices by customer type, the link customers
    // open from a chat. Shown to anyone who may manage the shop or set prices.
    to: '/shop', label: 'Shop',
    needs: PERMISSION.shopManage, orNeeds: PERMISSION.shopPrices,
    icon: (
      <>
        <path d="M3.5 7.5 5 4.5h10l1.5 3v1.2a2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.2 0z" {...stroke} />
        <path d="M4.5 9.8V16h11V9.8M8 16v-3.5h4V16" {...stroke} />
      </>
    ),
  },
  {
    // Back at the top level. It lived in the header's balance chip only because
    // the rail could not hold a sixth word.
    to: '/billing', label: 'Billing',
    needs: PERMISSION.reportsView,
    orNeeds: PERMISSION.settingsWrite,
    icon: (
      <>
        <rect x="3" y="5.4" width="14" height="9.2" rx="1.6" {...stroke} />
        <path d="M3 8.6h14" {...stroke} />
        <path d="M6 12.2h3" {...stroke} />
      </>
    ),
  },
  {
    // Next to Routing on purpose: a bot is the step before the rules, and the two
    // screens are read together — this one says what will answer, that one says who
    // gets it afterwards.
    to: '/bots', label: 'Bots',
    needs: PERMISSION.automationWrite,
    icon: (
      <>
        <rect x="4" y="6.5" width="12" height="9" rx="2.6" {...stroke} />
        <path d="M10 6.5V3.6" {...stroke} />
        <circle cx="10" cy="2.8" r="1" {...stroke} />
        <path d="M7.7 10.2v1.4M12.3 10.2v1.4" {...stroke} />
      </>
    ),
  },
  {
    // One entry for the whole settings area. Team, Routing and Settings were three
    // rail entries into one tabbed screen, at three different tabs — three glyphs
    // to remember for what is, on the reference, a single Settings destination
    // with the sections listed inside it by name.
    to: '/settings', label: 'Settings', onPhone: true,
    anySection: true,
    icon: (
      <>
        <circle cx="10" cy="10" r="2.4" {...stroke} />
        <path d="M10 3.4v1.8M10 14.8v1.8M16.6 10h-1.8M5.2 10H3.4M14.7 5.3l-1.3 1.3M6.6 13.4l-1.3 1.3M14.7 14.7l-1.3-1.3M6.6 6.6 5.3 5.3" {...stroke} />
      </>
    ),
  },
];

export function IconRail() {
  const { user } = useAuth();
  const [overflow, setOverflow] = useState(false);
  const [wide, setWide] = useState(false);

  // Hover only where a pointer can hover. On a touch screen the hover state is
  // whatever was tapped last, so the panel would open on the tap that navigated
  // and then sit over the screen it navigated to.
  const hoverable = useHoverablePointer();

  const items = ITEMS.filter((item) =>
    (!item.needs && !item.anySection)
      || (item.needs !== undefined && can(user, item.needs))
      || (item.orNeeds !== undefined && can(user, item.orNeeds))
      || (item.anySection === true && SECTIONS.some((section) => maySeeSection(user, section))));

  /*
   * The count beside Chats.
   *
   * Same query key as the inbox's and the shell's, so this is not a third request —
   * React Query serves all three from one fetch, and the number on the rail cannot
   * disagree with the number on the filter panel because it is the same number.
   *
   * Conversations with something unread, which is the count a person means by "how
   * much is waiting". Gated on the permission because the endpoint is: an agent
   * without inbox.view would get a 403 on every screen, and a rail that logs errors
   * is a rail that hides real ones.
   */
  const unread = useQuery({
    queryKey: ['counts'],
    queryFn: inboxApi.counts,
    enabled: can(user, PERMISSION.inboxView),
  }).data?.unread ?? 0;

  const onPhone = items.filter((i) => i.onPhone).slice(0, 4);
  const rest = items.filter((i) => !onPhone.includes(i));

  // The account menu holds the rail open. It is anchored to the rail's right
  // edge, and that edge moves 176px when the rail collapses — so a menu that let
  // the rail shut under it would slide across the screen while being read.
  const [accountMenu, setAccountMenu] = useState(false);
  const open = (wide || accountMenu) && hoverable;

  return (
    <>
      {/* Two elements, on purpose. This one holds the 56px the rail occupies in the
          layout and never changes width; the panel inside it is absolutely
          positioned and free to grow over the screen. Widening the flex child
          itself would reflow the four-column chat screen every time a pointer
          crossed the left edge. */}
      {/* A veil over everything else while the panel is open.
          Not decoration. The panel is 232px and whatever sits beside it is not,
          so a strip of the next column stays visible — on the chat screen that
          strip was the filter panel's right-aligned counts and its select
          chevrons, which read as a column of orphaned numbers rather than as a
          panel lying on top of something. The veil says which layer is on top.

          pointer-events-none, always: this closes when the pointer leaves the
          rail, and a veil that swallowed the pointer would leave it open with no
          way to shut it. */}
      <div
        aria-hidden="true"
        className={[
          'pointer-events-none fixed inset-0 z-30 hidden bg-black/25 transition-opacity duration-150',
          'motion-reduce:transition-none',
          open ? 'lg:block lg:opacity-100' : 'lg:hidden lg:opacity-0',
        ].join(' ')}
      />

      <div className="relative order-last shrink-0 lg:order-none lg:w-16">
        <nav
          aria-label="Sections"
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') setWide(true); }}
          onPointerLeave={() => setWide(false)}
          onFocus={() => setWide(true)}
          onBlur={(e) => {
            // Only when focus has actually left the rail — moving between two
            // items inside it fires blur on the first.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setWide(false);
          }}
          data-open={open ? 'true' : undefined}
          className={[
            'flex items-stretch gap-1 border-t border-(--color-line) bg-(--color-paper) px-2 py-1',
            'lg:absolute lg:inset-y-0 lg:left-0 lg:z-40 lg:flex-col lg:gap-0.5 lg:overflow-hidden',
            'lg:border-t-0 lg:border-r lg:px-2 lg:py-3',
            // 220ms with an ease-out, not 150 linear. The first version was fast
            // enough to read as a jump rather than as a movement, which is the
            // difference the reference has and this did not.
            'lg:transition-[width] lg:duration-200 lg:ease-out motion-reduce:lg:transition-none',
            open ? 'lg:w-60 lg:shadow-(--shadow-pop)' : 'lg:w-16',
          ].join(' ')}
        >
          {/* The tenant's own name heads the open panel, which is where the design
              being matched puts it. Hidden while collapsed rather than truncated to
              two letters, which reads as a bug. */}
          {/* Always rendered, clipped by the nav's own overflow while it is
              narrow. Mounting it on open made it appear at full opacity against a
              panel that was still growing — the text arrived before the box it
              sits in, which is exactly what "not smooth" looks like. */}
          <p
            aria-hidden={!open}
            className={[
              'mb-2 hidden h-4 shrink-0 truncate px-2 text-micro font-semibold tracking-wide',
              'text-(--color-muted) uppercase transition-opacity duration-200 ease-out',
              'motion-reduce:transition-none lg:block',
              open ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          >
            {user?.tenantName ?? 'PSM'}
          </p>

          {/* The rail shows everything; the bottom bar shows four and an overflow. */}
          {items.map((item) => (
            <RailLink
              key={item.to}
              item={item}
              phone={onPhone.includes(item)}
              open={open}
              count={item.to === '/chats' ? unread : 0}
            />
          ))}

        {rest.length > 0 && (
          <button
            type="button"
            onClick={() => setOverflow(true)}
            className="tap flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-(--radius-field) px-1 py-1.5 text-micro font-medium text-(--color-muted) lg:hidden"
          >
            <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
              <circle cx="4.5" cy="10" r="1.4" fill="currentColor" />
              <circle cx="10" cy="10" r="1.4" fill="currentColor" />
              <circle cx="15.5" cy="10" r="1.4" fill="currentColor" />
            </svg>
            <span className="truncate">More</span>
          </button>
        )}

        {/* One control at the foot, not two.
            A bare availability pill beside the avatar rendered as a stray empty
            circle at 56px — the word that carried its meaning does not fit, and a
            lone ring says nothing. The design being matched puts the state as a dot
            *on* the avatar, which is the same information attached to the thing it
            is about, and opens a menu for the actions. */}
          <div className="mt-auto hidden w-full lg:block">
            <AccountButton open={open} menuOpen={accountMenu} onMenuOpen={setAccountMenu} />
            <p
              aria-hidden={!open}
              className={[
                'mt-2 h-4 px-2 whitespace-nowrap text-micro text-(--color-faint)',
                'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                open ? 'opacity-100' : 'opacity-0',
              ].join(' ')}
            >
              Version {APP_VERSION}
            </p>
          </div>
        </nav>
      </div>

      {overflow && (
        <OverflowSheet items={rest} onClose={() => setOverflow(false)} />
      )}
    </>
  );
}

/** What the build stamped in, so a support call can start with a known version. */
const APP_VERSION = __APP_VERSION__;

/**
 * Whether this pointer can hover at all.
 *
 * A media query and not a touch-event sniff: the question is about the pointer
 * in use, and a laptop with a touchscreen answers it correctly either way. It is
 * live, because a tablet with a keyboard case attached changes the answer without
 * reloading the page.
 */
function useHoverablePointer(): boolean {
  const [can_, setCan] = useState(() =>
    typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCan(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return can_;
}

function RailLink({ item, phone, open, count = 0 }: {
  item: Item; phone: boolean; open: boolean; count?: number;
}) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => [
        'tap group relative flex min-w-0 items-center rounded-(--radius-field) transition-colors',
        // Bottom bar: icon over label, four across.
        'flex-1 flex-col justify-center gap-0.5 px-1 py-1.5 text-micro font-medium',
        phone ? '' : 'hidden',
        // Rail: the same row in both states — full width, icon at a fixed left
        // offset, label after it. The nav clips the label while it is narrow.
        //
        // The icon must not MOVE when the panel opens. It did, because the
        // collapsed link was a centred 40px box and the open one was a
        // left-aligned full-width box: every glyph slid a few pixels sideways
        // while the panel grew, which reads as the whole rail wobbling.
        'lg:flex lg:h-10 lg:w-full lg:flex-none lg:flex-row lg:justify-start lg:gap-2.5 lg:px-2.5 lg:py-0 lg:text-body',
        isActive
          // A pale brand wash with brand-coloured text and glyph — what the design
          // being matched does, and what the previous filled-square version got
          // wrong. A filled square is louder than every other thing on the rail
          // and reads as a button rather than as "you are here".
          ? 'bg-(--color-brand-wash) text-(--color-brand)'
          : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
      ].join(' ')}
    >
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" className="shrink-0">
        {item.icon}
      </svg>

      {/* Always laid out, never `sr-only`: taking it out of layout made it snap
          back in on open, ahead of the width it belongs to. It fades instead, and
          the nav's overflow does the clipping. It is the link's accessible name in
          both states, so the rail reads the same to a screen reader either way. */}
      <span className={[
        'truncate transition-opacity duration-150 ease-out motion-reduce:transition-none',
        'lg:whitespace-nowrap',
        open ? 'lg:opacity-100' : 'lg:opacity-0',
      ].join(' ')}>
        <span className="lg:hidden">{item.phoneLabel ?? item.label}</span>
        <span className="hidden lg:inline">{item.label}</span>
      </span>

      {/* No tooltip while the panel is open — the row already says it, and the
          tip would land on top of the row below. */}
      {!open && <Tip>{item.label}</Tip>}

      {count > 0 && <Count value={count} open={open} />}
    </NavLink>
  );
}

/**
 * How much is waiting.
 *
 * Two positions, because the rail is two things. Collapsed, it is a pip pinned to
 * the top-right of the glyph — the only place a number fits in 56px, and the
 * convention every app with a dock icon uses. Open, it moves to the right-hand end
 * of the row, where a count belongs beside a label rather than on top of an icon
 * that now has room for words.
 *
 * <b>The number is not the only signal.</b> `aria-label` says it in words, because a
 * red pip is invisible to a screen reader and roughly one man in twelve reads the
 * colour differently from the way it was intended. It is `aria-live="polite"` so a
 * count that changes while the agent is elsewhere in the app is announced rather
 * than silently rewritten — this is the one thing on the rail that changes without
 * anybody touching it.
 */
function Count({ value, open }: { value: number; open: boolean }) {
  const label = `${value} unread ${value === 1 ? 'chat' : 'chats'}`;

  return (
    <span
      aria-label={label}
      aria-live="polite"
      className={[
        'pointer-events-none min-w-4 rounded-full bg-(--color-bad) px-1 text-center',
        'text-micro font-semibold text-white tabular-nums',
        // Collapsed: a pip over the glyph, on both the phone bar and the narrow rail.
        // The ring is the surface colour, so the pip reads as floating above the icon
        // rather than as part of it.
        'absolute top-0.5 left-1/2 ml-1.5 ring-2 ring-(--color-paper)',
        // Open: in the row, at the end, no ring and no overlap.
        open
          ? 'lg:static lg:top-auto lg:left-auto lg:ml-auto lg:ring-0'
          : '',
      ].join(' ')}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

/**
 * The label, on hover and on focus.
 *
 * `group-focus-visible` as well as `group-hover`: a rail somebody is tabbing
 * through is a rail of unlabelled glyphs unless the keyboard gets the same
 * affordance the mouse does.
 */
function Tip({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="
        pointer-events-none absolute left-full z-50 ml-2 hidden whitespace-nowrap
        rounded-(--radius-field) bg-(--color-ink) px-2 py-1 text-micro font-medium
        text-(--color-surface) opacity-0 shadow-sm transition-opacity
        group-hover:opacity-100 group-focus-visible:opacity-100 lg:block
      "
    >
      {children}
    </span>
  );
}

/** The destinations that do not fit a phone's bottom bar. */
function OverflowSheet({ items, onClose }: { items: Item[]; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40 lg:hidden"
      role="dialog"
      aria-label="More sections"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-(--radius-card) border-t border-(--color-line) bg-(--color-surface) p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="grid grid-cols-2 gap-1">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onClose}
                className="tap flex items-center gap-2.5 rounded-(--radius-field) px-3 py-2.5 text-body text-(--color-ink-soft) hover:bg-(--color-sunken)"
              >
                <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" className="shrink-0">
                  {item.icon}
                </svg>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * The avatar at the foot of the rail, carrying the availability dot and opening
 * the account menu.
 *
 * Availability and signing out are two different actions and the avatar is one
 * target, so it opens a menu rather than doing either. Making the avatar itself
 * toggle availability would put an irreversible-looking state change on the
 * control somebody clicks to find "sign out".
 */
function AccountButton({ open: wide, menuOpen, onMenuOpen }: {
  open: boolean;
  menuOpen: boolean;
  onMenuOpen: (open: boolean) => void;
}) {
  const { user, signOut } = useAuth();
  const open = menuOpen;
  const setOpen = onMenuOpen;
  const root = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; bottom: number } | null>(null);

  // `position: fixed`, measured from the rail, rather than `absolute left-full`.
  //
  // The nav carries `lg:overflow-hidden` — that is what clips the labels while
  // the rail is narrow, and it is the whole reason the hover-expand looks like
  // anything. A menu positioned outside the nav's box is therefore clipped away
  // completely: it opened, it was in the DOM, and nobody could see it. That is
  // how this went missing without an error anywhere.
  //
  // Fixed escapes the clip (no ancestor creates a containing block here), and
  // measuring on open rather than hard-coding 56px means it lands correctly
  // whether the rail is collapsed or expanded.
  useLayoutEffect(() => {
    if (!open) { setAt(null); return; }
    const nav = root.current?.closest('nav');
    if (!nav) return;

    const place = () => {
      const box = nav.getBoundingClientRect();
      setAt({
        left: Math.round(box.right + 8),
        bottom: Math.round(window.innerHeight - box.bottom + 12),
      });
    };

    place();

    // And again when the rail has finished growing. The nav animates its width
    // over 200ms, and `getBoundingClientRect` during a transition returns the
    // width *right now* — so measuring once on open anchored the menu to the
    // collapsed 56px rail and dropped it on top of the expanded 232px one. It
    // looked like it worked, which is the worst way for it to be wrong.
    const onEnd = (e: TransitionEvent) => { if (e.propertyName === 'width') place(); };
    nav.addEventListener('transitionend', onEnd);
    return () => nav.removeEventListener('transitionend', onEnd);
  }, [open, wide]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // A menu pinned to a measured rectangle has to go when the rectangle can move.
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  const availability = useQuery({
    queryKey: ['availability'],
    queryFn: inboxApi.availability,
    staleTime: 60_000,
  });

  const available = availability.data?.availability === 1;
  const name = user?.displayName ?? 'Account';

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${name} — ${available ? 'available' : 'away'}`}
        title={`${name} — ${available ? 'available' : 'away'}`}
        // One shape in both states, for the same reason the links have one: a
        // control that re-centres itself while the panel grows reads as a wobble.
        //
        // px-1 and a 32px avatar, not px-1.5 and 36px: the collapsed rail leaves
        // 40px inside its own padding, and a 36px avatar with 6px either side
        // needs 48. It escaped its button by eight pixels — caught by the layout
        // check rather than by looking, because eight pixels of overlap on a
        // rounded avatar is invisible until it is measured.
        className="tap flex w-full items-center gap-2.5 rounded-(--radius-field) px-1 py-1.5 transition-colors hover:bg-(--color-sunken)"
      >
        <span className="relative grid size-8 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-small font-semibold text-(--color-brand)">
          {name.trim().charAt(0).toUpperCase()}

          {/* The ring is the rail's own background, so the dot reads as sitting on
              top of the avatar rather than punched out of it. */}
          <span
            aria-hidden="true"
            className={[
              'absolute right-0 bottom-0 size-3 rounded-full ring-2 ring-(--color-paper)',
              available ? 'bg-(--color-good)' : 'bg-(--color-faint)',
            ].join(' ')}
          />
        </span>

        <span className={[
          'min-w-0 flex-1 text-left whitespace-nowrap',
          'transition-opacity duration-150 ease-out motion-reduce:transition-none',
          wide ? 'opacity-100' : 'opacity-0',
        ].join(' ')}>
          <span className="block truncate text-small font-medium text-(--color-ink)">{name}</span>
          <span className={[
            'block truncate text-micro',
            available ? 'text-(--color-good)' : 'text-(--color-muted)',
          ].join(' ')}>
            {available ? 'Online' : 'Away'}
          </span>
        </span>
      </button>

      {open && (
        <>
          {/* A click anywhere else closes it. Without this the menu stays open
              behind whatever the next click opened. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />

          <div
            role="menu"
            style={at ? { left: at.left, bottom: at.bottom } : undefined}
            className={[
              'fixed z-50 w-56 rounded-(--radius-card) border border-(--color-line)',
              'bg-(--color-surface) p-2 shadow-(--shadow-pop)',
              // Until it has been measured it must not be drawn at 0,0.
              at ? '' : 'invisible',
            ].join(' ')}
          >
            <p className="truncate px-2 pt-1 text-small font-medium">{name}</p>
            <p className="truncate px-2 pb-2 text-micro text-(--color-muted)">{user?.email}</p>

            <div className="border-t border-(--color-line) px-2 py-2">
              <AvailabilityToggle />
            </div>

            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); void signOut(); }}
              className="tap flex w-full items-center gap-2 rounded-(--radius-field) border-t border-(--color-line) px-2 py-2 text-left text-small text-(--color-ink-soft) hover:bg-(--color-sunken)"
            >
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M8 16.5H4.8a1.3 1.3 0 0 1-1.3-1.3V4.8a1.3 1.3 0 0 1 1.3-1.3H8" {...stroke} />
                <path d="M12.6 13.2 16 10l-3.4-3.2M16 10H7.4" {...stroke} />
              </svg>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
